import React, { useState, useEffect } from 'react';
import { Search, MapPin, Wifi, AlertCircle, Layers, Flame, Circle as CircleIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import 'leaflet.heat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

delete L.Icon.Default.prototype._getIconUrl;

const createIcon = (color) => new L.DivIcon({
  className: 'cm-marker',
  html: `<div style="
    width: 22px; height: 22px; border-radius: 50% 50% 50% 0;
    background: ${color}; transform: rotate(-45deg);
    border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    display: flex; align-items: center; justify-content: center;
  "><div style="
    width: 7px; height: 7px; background: white; border-radius: 50%;
    transform: rotate(45deg);
  "></div></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
  popupAnchor: [0, -22],
});

const COLORS = {
  online: '#10b981',
  offline: '#94a3b8',
  isolir: '#ef4444',
  belum_bayar: '#f59e0b',
};

const getCustomerStatus = (c) => {
  if (!c.is_active) return 'isolir';
  if (c.payment_status === 'belum_bayar') return 'belum_bayar';
  if (c.is_online) return 'online';
  return 'offline';
};

const statusLabels = {
  online: 'Online',
  offline: 'Offline',
  isolir: 'Isolir',
  belum_bayar: 'Belum Bayar',
};

// Build popup HTML for a customer
const buildPopupHTML = (c, status) => `
  <div style="font-size:12px;min-width:200px">
    <div style="font-weight:bold;font-size:13px;color:#1e293b">${c.name || '-'}</div>
    <div style="color:#64748b;font-size:11px">${c.account_number || ''}</div>
    <div style="margin:4px 0"><span style="display:inline-block;padding:2px 6px;border-radius:4px;border:1px solid ${COLORS[status]};color:${COLORS[status]};font-size:10px">${statusLabels[status]}</span></div>
    ${c.service_plan_name ? `<div><strong>Paket:</strong> ${c.service_plan_name}</div>` : ''}
    ${c.pppoe_username ? `<div><strong>PPPoE:</strong> ${c.pppoe_username}</div>` : ''}
    ${c.address ? `<div style="color:#475569">${c.address}</div>` : ''}
    ${c.phone ? `<div><strong>Telp:</strong> ${c.phone}</div>` : ''}
    ${c.branch_name ? `<div><strong>Cabang:</strong> ${c.branch_name}</div>` : ''}
    <a href="https://www.google.com/maps?q=${c.latitude},${c.longitude}" target="_blank" rel="noopener" style="color:#2563eb;font-size:11px;display:block;margin-top:4px">Buka di Google Maps →</a>
  </div>`;

// Cluster layer using leaflet.markercluster
const ClusterLayer = ({ customers, highlightProblems }) => {
  const map = useMap();
  useEffect(() => {
    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 60,
      iconCreateFunction: (clusterObj) => {
        const markers = clusterObj.getAllChildMarkers();
        const count = markers.length;
        // Compute composition
        let problems = 0;
        markers.forEach(m => {
          const s = m.options._status;
          if (s === 'isolir' || s === 'belum_bayar') problems++;
        });
        const ratio = problems / count;
        // Color: heavy problem ratio → red, mostly online → green, mixed → blue
        let bg = '#3b82f6'; // default blue
        if (highlightProblems) {
          if (ratio > 0.5) bg = '#ef4444';
          else if (ratio > 0.2) bg = '#f59e0b';
          else bg = '#10b981';
        } else {
          if (count >= 100) bg = '#7c3aed';
          else if (count >= 20) bg = '#3b82f6';
          else bg = '#10b981';
        }
        const size = count >= 100 ? 50 : count >= 20 ? 42 : 36;
        return L.divIcon({
          html: `<div style="background:${bg};width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:${count>=100?13:12}px;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3)">${count}${highlightProblems && ratio > 0.2 ? `<div style="position:absolute;bottom:-2px;right:-2px;background:white;color:${bg};border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:10px;border:2px solid ${bg}">⚠</div>` : ''}</div>`,
          className: 'cm-cluster-icon',
          iconSize: [size, size],
        });
      },
    });
    customers.forEach(c => {
      const status = getCustomerStatus(c);
      const m = L.marker([c.latitude, c.longitude], {
        icon: createIcon(COLORS[status]),
        _status: status,
      });
      m.bindPopup(buildPopupHTML(c, status));
      cluster.addLayer(m);
    });
    map.addLayer(cluster);
    return () => { map.removeLayer(cluster); };
  }, [customers, highlightProblems, map]);
  return null;
};

// Heatmap layer using leaflet.heat
const HeatmapLayer = ({ customers, mode }) => {
  const map = useMap();
  useEffect(() => {
    let points;
    if (mode === 'problems') {
      // Higher weight for isolir/belum_bayar
      points = customers.map(c => {
        const s = getCustomerStatus(c);
        const weight = s === 'isolir' ? 1.0 : s === 'belum_bayar' ? 0.7 : 0.05;
        return [c.latitude, c.longitude, weight];
      });
    } else {
      // Density: equal weight
      points = customers.map(c => [c.latitude, c.longitude, 0.5]);
    }
    const layer = L.heatLayer(points, {
      radius: 25,
      blur: 18,
      maxZoom: 17,
      gradient: mode === 'problems'
        ? { 0.0: '#10b981', 0.4: '#f59e0b', 0.7: '#ef4444', 1.0: '#7f1d1d' }
        : { 0.0: '#3b82f6', 0.5: '#8b5cf6', 1.0: '#ef4444' },
    });
    map.addLayer(layer);
    return () => { map.removeLayer(layer); };
  }, [customers, mode, map]);
  return null;
};

const FitBounds = ({ markers, viewMode }) => {
  const map = useMap();
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.latitude, m.longitude]));
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 13 });
    }
  }, [markers, viewMode, map]);
  return null;
};

const ODP_COLORS = {
  active: '#0891b2',     // cyan
  full: '#dc2626',       // red
  maintenance: '#d97706',// amber
  inactive: '#475569',   // slate
};

const odpStatusLabels = {
  active: 'Aktif', full: 'Penuh', maintenance: 'Maintenance', inactive: 'Nonaktif',
};

// Square-shaped ODP icon (different from customer teardrop)
const createOdpIcon = (color) => new L.DivIcon({
  className: 'odp-marker',
  html: `<div style="
    width: 26px; height: 26px;
    background: ${color}; border-radius: 4px;
    border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    display: flex; align-items: center; justify-content: center;
    color: white; font-weight: bold; font-size: 12px;
  ">O</div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  popupAnchor: [0, -13],
});

const CustomerMap = () => {
  const [customers, setCustomers] = useState([]);
  const [odps, setOdps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [viewMode, setViewMode] = useState('cluster'); // 'markers' | 'cluster' | 'heatmap-density' | 'heatmap-problems'
  const [highlightProblems, setHighlightProblems] = useState(false);
  const [showODP, setShowODP] = useState(true);
  const [showCustomers, setShowCustomers] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchODP = async () => {
    try {
      const res = await fetch(`${API_URL}/api/odp-pop/map-data`, { headers });
      const data = await res.json();
      if (data.success) setOdps(data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      const res = await fetch(`${API_URL}/api/customers/map-data?${params}`, { headers });
      const data = await res.json();
      if (data.success) setCustomers(data.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/customers/map-statistics`, { headers });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchData(); }, [filters.status]);
  useEffect(() => { fetchStats(); fetchODP(); }, []);

  const center = customers.length > 0
    ? [customers[0].latitude, customers[0].longitude]
    : [-6.2088, 106.8456];

  const isHeatmap = viewMode.startsWith('heatmap');
  const isCluster = viewMode === 'cluster';
  const isMarkers = viewMode === 'markers';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 space-y-4 p-3 sm:p-5 lg:p-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200">Peta Pelanggan & ODP/POP</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Visualisasi gabungan pelanggan dan infrastruktur jaringan</p>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
          <span className="flex items-center gap-1" data-testid="customer-map-count">
            <MapPin className="w-4 h-4" /> {customers.length} pelanggan
          </span>
          <span className="flex items-center gap-1" data-testid="odp-map-count">
            <div className="w-3 h-3 rounded-sm bg-cyan-600"></div> {odps.length} ODP/POP
          </span>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-white border rounded-lg p-3 dark:bg-slate-800">
            <div className="text-xs text-slate-500 dark:text-slate-400">Total Pelanggan</div>
            <div className="text-xl font-bold text-slate-800 dark:text-slate-200">{stats.total || 0}</div>
          </div>
          <div className="bg-white border rounded-lg p-3 dark:bg-slate-800">
            <div className="text-xs text-slate-500 dark:text-slate-400">Punya Koordinat</div>
            <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{stats.with_coord || 0}</div>
          </div>
          <div className="bg-white border rounded-lg p-3 dark:bg-slate-800">
            <div className="text-xs text-slate-500 flex items-center gap-1 dark:text-slate-400"><Wifi className="w-3 h-3"/> Online</div>
            <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{stats.online || 0}</div>
          </div>
          <div className="bg-white border rounded-lg p-3 dark:bg-slate-800">
            <div className="text-xs text-slate-500 flex items-center gap-1 dark:text-slate-400"><AlertCircle className="w-3 h-3"/> Isolir</div>
            <div className="text-xl font-bold text-red-600 dark:text-red-400">{stats.isolir || 0}</div>
          </div>
          <div className="bg-white border rounded-lg p-3 dark:bg-slate-800">
            <div className="text-xs text-slate-500 dark:text-slate-400">Belum Bayar</div>
            <div className="text-xl font-bold text-amber-600 dark:text-amber-400">{stats.belum_bayar || 0}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center bg-white border rounded-lg p-3 dark:bg-slate-800">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <Input
            data-testid="customer-map-search"
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter') fetchData(); }}
            placeholder="Cari nama, alamat, atau username PPPoE..."
            className="pl-9"
          />
        </div>
        <select
          data-testid="customer-map-filter-status"
          value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="h-10 border rounded-md px-3 text-sm bg-white dark:bg-slate-800"
        >
          <option value="">Semua Status</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="isolir">Isolir</option>
          <option value="belum_bayar">Belum Bayar</option>
        </select>
        <Button onClick={fetchData} data-testid="customer-map-search-btn">Cari</Button>
      </div>

      {/* Layer Toggle */}
      <div className="flex flex-wrap gap-3 items-center bg-white border rounded-lg p-3 dark:bg-slate-800">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Tampilkan Layer:</span>
        <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer dark:text-slate-200">
          <input
            type="checkbox"
            data-testid="toggle-customers"
            checked={showCustomers}
            onChange={e => setShowCustomers(e.target.checked)}
            className="rounded"
          />
          <span>Pelanggan ({customers.length})</span>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer dark:text-slate-200">
          <input
            type="checkbox"
            data-testid="toggle-odp"
            checked={showODP}
            onChange={e => setShowODP(e.target.checked)}
            className="rounded"
          />
          <span>ODP/POP ({odps.length})</span>
        </label>
      </div>

      {/* View Mode Tabs */}
      <div className="bg-white border rounded-lg p-3 dark:bg-slate-800">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Tampilan:</span>
          {isCluster && (
            <label className="flex items-center gap-1.5 text-xs text-slate-700 ml-auto cursor-pointer dark:text-slate-200">
              <input
                type="checkbox"
                data-testid="cluster-highlight-problems"
                checked={highlightProblems}
                onChange={e => setHighlightProblems(e.target.checked)}
                className="rounded"
              />
              <span className="hidden sm:inline">Highlight area bermasalah</span>
              <span className="sm:hidden">Highlight</span>
            </label>
          )}
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          <button
            onClick={() => setViewMode('cluster')}
            data-testid="vm-cluster"
            className={`text-xs px-3 py-2 rounded-md flex items-center justify-center sm:justify-start gap-1.5 transition ${isCluster ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            <Layers className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="hidden sm:inline">Cluster (Recommended)</span>
            <span className="sm:hidden">Cluster</span>
          </button>
          <button
            onClick={() => setViewMode('markers')}
            data-testid="vm-markers"
            className={`text-xs px-3 py-2 rounded-md flex items-center justify-center sm:justify-start gap-1.5 transition ${isMarkers ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            <CircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="hidden sm:inline">Marker Individual</span>
            <span className="sm:hidden">Marker</span>
          </button>
          <button
            onClick={() => setViewMode('heatmap-density')}
            data-testid="vm-heatmap"
            className={`text-xs px-3 py-2 rounded-md flex items-center justify-center sm:justify-start gap-1.5 transition ${viewMode === 'heatmap-density' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            <Flame className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="hidden sm:inline">Heatmap Kepadatan</span>
            <span className="sm:hidden">Kepadatan</span>
          </button>
          <button
            onClick={() => setViewMode('heatmap-problems')}
            data-testid="vm-heatmap-problems"
            className={`text-xs px-3 py-2 rounded-md flex items-center justify-center sm:justify-start gap-1.5 transition ${viewMode === 'heatmap-problems' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            <Flame className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="hidden sm:inline">Heatmap Bermasalah</span>
            <span className="sm:hidden">Bermasalah</span>
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 items-center text-xs text-slate-600 bg-white border rounded-lg p-3 dark:text-slate-300 dark:bg-slate-800">
        <span className="font-medium text-slate-700 dark:text-slate-200">Legenda:</span>
        {viewMode === 'heatmap-problems' ? (
          <>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{background:'#10b981'}}/>Normal</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{background:'#f59e0b'}}/>Belum bayar</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{background:'#ef4444'}}/>Banyak isolir</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{background:'#7f1d1d'}}/>Critical</span>
          </>
        ) : viewMode === 'heatmap-density' ? (
          <>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{background:'#3b82f6'}}/>Sepi</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{background:'#8b5cf6'}}/>Sedang</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{background:'#ef4444'}}/>Padat</span>
          </>
        ) : isCluster && highlightProblems ? (
          <>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full" style={{background:'#10b981'}}/>Sehat (&lt;20% bermasalah)</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full" style={{background:'#f59e0b'}}/>Perhatian (20-50%)</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full" style={{background:'#ef4444'}}/>Critical (&gt;50%)</span>
          </>
        ) : (
          Object.entries(statusLabels).map(([key, label]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ background: COLORS[key] }} />
              <span>{label}</span>
            </div>
          ))
        )}
        {showODP && (
          <>
            <span className="border-l border-slate-300 h-4 mx-1 dark:border-slate-600"></span>
            <span className="text-slate-600 font-medium dark:text-slate-300">ODP/POP:</span>
            {Object.entries(odpStatusLabels).map(([key, label]) => (
              <div key={`odp-${key}`} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm" style={{ background: ODP_COLORS[key] }} />
                <span>{label}</span>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Map */}
      <div className="bg-white border rounded-lg overflow-hidden dark:bg-slate-800" style={{ height: '600px' }}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-slate-500 dark:text-slate-400">Memuat peta...</div>
        ) : customers.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-2 dark:text-slate-400">
            <MapPin className="w-12 h-12 text-slate-300" />
            <p>Belum ada pelanggan dengan koordinat valid</p>
          </div>
        ) : (
          <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds markers={[...(showCustomers ? customers : []), ...(showODP ? odps : [])]} viewMode={viewMode} />
            {showCustomers && isMarkers && customers.map(c => {
              const status = getCustomerStatus(c);
              return (
                <Marker
                  key={`c-${c.id}`}
                  position={[c.latitude, c.longitude]}
                  icon={createIcon(COLORS[status])}
                >
                  <Popup>
                    <div className="text-sm space-y-1 min-w-[200px]">
                      <div className="font-bold text-slate-800 dark:text-slate-200">{c.name}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{c.account_number}</div>
                      <Badge variant="outline" style={{ borderColor: COLORS[status], color: COLORS[status] }}>
                        {statusLabels[status]}
                      </Badge>
                      {c.service_plan_name && <div className="text-xs"><strong>Paket:</strong> {c.service_plan_name}</div>}
                      {c.pppoe_username && <div className="text-xs"><strong>PPPoE:</strong> {c.pppoe_username}</div>}
                      {c.address && <div className="text-xs text-slate-600 dark:text-slate-300">{c.address}</div>}
                      {c.phone && <div className="text-xs"><strong>Telp:</strong> {c.phone}</div>}
                      {c.branch_name && <div className="text-xs"><strong>Cabang:</strong> {c.branch_name}</div>}
                      <a href={`https://www.google.com/maps?q=${c.latitude},${c.longitude}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block mt-1 dark:text-blue-400">
                        Buka di Google Maps →
                      </a>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {showCustomers && isCluster && <ClusterLayer customers={customers} highlightProblems={highlightProblems} />}
            {showCustomers && isHeatmap && <HeatmapLayer customers={customers} mode={viewMode === 'heatmap-problems' ? 'problems' : 'density'} />}
            {/* ODP/POP markers (always rendered as individual markers regardless of view mode) */}
            {showODP && odps.map(o => {
              const odpColor = ODP_COLORS[o.status] || ODP_COLORS.active;
              const usedRatio = o.capacity > 0 ? (o.used_ports || 0) / o.capacity : 0;
              return (
                <Marker
                  key={`o-${o.id}`}
                  position={[o.latitude, o.longitude]}
                  icon={createOdpIcon(odpColor)}
                >
                  <Popup>
                    <div className="text-sm space-y-1 min-w-[220px]">
                      <div className="flex items-center gap-2">
                        <div style={{ background: odpColor }} className="w-4 h-4 rounded-sm flex items-center justify-center text-white text-[9px] font-bold">O</div>
                        <div className="font-bold text-slate-800 dark:text-slate-200">{o.name}</div>
                      </div>
                      <Badge variant="outline" style={{ borderColor: odpColor, color: odpColor }}>
                        ODP/POP - {odpStatusLabels[o.status] || o.status}
                      </Badge>
                      {o.address && <div className="text-xs text-slate-600 dark:text-slate-300">{o.address}</div>}
                      <div className="text-xs"><strong>Kapasitas:</strong> {o.used_ports || 0}/{o.capacity || 0} port ({Math.round(usedRatio * 100)}%)</div>
                      {o.linked_customers !== undefined && <div className="text-xs"><strong>Pelanggan terhubung:</strong> {o.linked_customers}</div>}
                      {o.branch_name && <div className="text-xs"><strong>Cabang:</strong> {o.branch_name}</div>}
                      {o.owner_name && <div className="text-xs"><strong>Owner:</strong> {o.owner_name}</div>}
                      <a href={`https://www.google.com/maps?q=${o.latitude},${o.longitude}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block mt-1 dark:text-blue-400">
                        Buka di Google Maps →
                      </a>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default CustomerMap;
